<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import localforage from "localforage";
import { onMounted, onUnmounted } from "vue";
import { type EvidenceTheme } from "@/models/EvidenceTheme";
import type { Evidence } from "@/models/Evidence";
import { useEvidenceStore } from "@/stores/evidence-store";
import { trimEnd, getEvidenceText } from "@/services/UtilitiesService";

withDefaults(defineProps<{
  brand: string;
  casename: string;
  category: string;
}>(), {  })

let categories = _ref<EvidenceTheme[] | null>(null);
let entries = _ref<Evidence[] | null>(null);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const loading = _computed(() => {
  return useEvidenceStore().loadingCategories && useEvidenceStore().loadingEvidence;
});
const items = _computed(() => {
  if (!entries.value) {
    return [];
  }

  return entries.value.filter((e) => e.theme.toLowerCase() === selectedCategory.value.toLowerCase());
});
const selectedCategory = _computed(() => {
  if (!categories.value) {
    return "";
  }

  const cat = categories.value.find((f) => f.code.toLowerCase() === __props.category.toLowerCase());

  return cat ? cat.name : "";
});

onMounted((): void => {
  getData(true).then(() => {
    window.addEventListener("storage", () => void getData());
  });
});

function getCategories(): Promise<EvidenceTheme[] | null> {
  return localforage.getItem<EvidenceTheme[]>(keyMethod([__props.brand]));
}
function getEvidence(): Promise<Evidence[] | null> {
  return localforage.getItem<Evidence[]>(keyMethod([__props.brand, __props.casename]));
}
async function getData(initial?: boolean): Promise<void> {
  categories.value = await (initial ? useEvidenceStore().loadCategories() : getCategories());
  entries.value = await (initial ? useEvidenceStore().loadEvidence() : getEvidence());
}

function keyMethod(keys: string[]): string {
  return keys.map((e) => (e ? e.toLowerCase() : "")).join("-");
}

function getEvidenceTitle(item: Evidence): string {
  return `${item.annotation ? item.annotation + "\n" : ""}${
    item.displayName
  }\n${item.edxFilename}\nType: ${item.type}\nValue: ${item.value}`;
}

function showEntry(entry: Evidence): void {
  const url = `${trimEnd(
    document.getElementsByTagName("base")[0].href,
    "/",
  )}/${__props.brand}/${__props.casename}/${entry.fileReference}/${entry.type}-${entry.value}`;
  window.open(
    url,
    "BZGEDXVIEW",
    "height=768,width=1336,location=no,resizable=yes,scrollbars=yes,menubar=no,status=yes,titlebar=no,toolbar=no",
  );
}
onUnmounted((): void => {
  window.removeEventListener("storage", () => getData);
});
</script>
<template>
  <div>
    <h5 v-if="items.length === 0">No evidence has been bookmarked</h5>
    <div v-else class="edxEVTable">
      <div class="edxEVTableBody">
        <div v-for="(entry, index) in items" :key="`e-entry-${index}`" class="edxEVTableRow">
          <div class="edxEVTableCell" :title="getEvidenceTitle(entry)">
            {{ getEvidenceText(entry) }}
          </div>
          <div class="edxEVTableCell link" @click="showEntry(entry)">Show</div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
body {
  height: 10px;
}

.edxEVWrapper {
  position: relative;
  font-family: "Trebuchet MS", "Lucida Sans Unicode", "Lucida Grande", "Lucida Sans", Arial, sans-serif;
  font-size: 13px;
  background-color: #fff;
}

.edxEVTable {
  background-color: #eeeeee;
  width: 100%;
  text-align: left;
  border-collapse: collapse;
  display: table;
}

.edxEVTableBody {
  display: table-row-group;
}

.edxEVTableRow {
  display: table-row;
  vertical-align: middle;
}

.edxEVTableCell {
  border: 1px solid #aaaaaa;
  padding: 3px 2px;
  display: table-cell;
  &.link {
    background: #d9d9d9;
    cursor: pointer;
    width: 120px;
    text-align: center;
  }
}

.edxEVTableRow:nth-child(even) .edxEVTableCell.link {
  background: #bbbbbb;
}

.edxEVTableRow:nth-child(even) .edxEVTableCell.link:hover,
.edxEVTableCell.link:hover {
  background: #202020;
  color: #fff;
}
</style>
